import React, { type ChangeEvent, useState } from 'react'

import { LntInput, LntSelect } from '@app/components/base'
import { TopFilters as TopFiltersWrapper } from '@app/components'

import { FILTERS_PARAMS_KEYS } from '@app/api/constants'
import { FILTERS_PLACEHOLDERS } from '@app/constants/filters'
import { LOCATION_KEY, MONITORINGS_LISTS_KEY, MONITORINGS_TYPES_KEY } from '@app/constants/queryKeys'

import { getKinds, getTypes } from '@app/api/routes/monitorings'
import { getStores } from '@app/api/routes/locationStores'
import { getClusters } from '@app/api/routes/locationСlusters'
import { getClusterGroups } from '@app/api/routes/locationClusterGroups'
import { getSalesOrganization } from '@app/api/routes/locationSalesOrganization'
import { getMonitoringListsPromo } from '@app/api/routes/monitoringListsPromo'

import { useFiltersSearch } from '@app/hooks'
import { getFilterFromEntities, getFilterFromEntitiesToLabelsIds } from '@app/utils/entitiesAdapters'

import { SIZE } from '@app/constants'
import { stringWithComma } from '@app/utils/string'
import { updateFiltersByKey } from '@app/pages/ProcessingResultsPage/ui/TopFilters/utils'

import { commonSelectProps } from './configs'

import type { TopFilterProps } from './types'

export const TopFilters: React.FC<TopFilterProps> = ({ onSearch, initialFilters }) => {
  const { filters, setFilters, canResetFilters, handleClearFilters, handleSearch, handleSelectFilterChange } =
    useFiltersSearch({
      initialFilters,
      onSearch
    })

  const [parsedProductCodeValue, setParsedProductCodeValue] = useState(filters?.productCodes)

  const onChangeInputProductCode = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value

    setParsedProductCodeValue(value)

    const parsedString = stringWithComma(value)

    setFilters(prev => updateFiltersByKey(prev, FILTERS_PARAMS_KEYS.productCodes, parsedString))
  }

  return (
    <TopFiltersWrapper
      canReset={ canResetFilters }
      onClear={ handleClearFilters }
      onSearch={ handleSearch }
    >
      <LntSelect
        { ...commonSelectProps }
        value={ filters.monitoringKindIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.monitoringKindIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.monitoringsKindFilter }
        querySelect={ {
          queryKey: [MONITORINGS_TYPES_KEY.kindsItems],
          queryFn: getKinds,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.monitoringTypeIds ?? [] }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.monitoringTypeIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.monitoringsTypeFilter }
        querySelect={ {
          queryKey: [MONITORINGS_TYPES_KEY.typesItems],
          queryFn: getTypes,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.storeIds ?? [] }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.storeIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.storesFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationStoresItems],
          queryFn: getStores,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.clusterIds ?? [] }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.clusterIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.clustersFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationClustersItems],
          queryFn: getClusters,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.clustersGroupIds ?? [] }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.clustersGroupIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.clusterGroupsFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationClusterGroupsItems],
          queryFn: getClusterGroups,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.promoIds ?? [] }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.promoIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.monitoringListsPromoFilter }
        querySelect={ {
          queryKey: [MONITORINGS_LISTS_KEY.promotion],
          queryFn: getMonitoringListsPromo,
          select: getFilterFromEntitiesToLabelsIds
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.salesOrganizationIds ?? [] }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.salesOrganizationIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.salesOrganizationFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationSalesOrganizationsItems],
          queryFn: getSalesOrganization,
          select: getFilterFromEntities
        } }
      />

      <LntInput
        value={ parsedProductCodeValue }
        onChange={ event => onChangeInputProductCode(event) }
        placeholder={ FILTERS_PLACEHOLDERS.productCode }
        size={ SIZE.middle }
      />
    </TopFiltersWrapper>
  )
}
